import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BridgeMode } from '@hg-com/xwc-js-bridge';
import { PermissionState } from '@hg-com/xwc-js-bridge/dist/modules';
import { Subscription } from 'rxjs';
import { DataService } from 'src/core/services/data.service';
import { LanguageService } from 'src/core/services/language.service';
import { NativeIntegrationService } from 'src/core/services/native-integration.service';
import { CollectionsService } from 'src/modules/collections/service/collections.service';
import { sleep } from 'src/util';

const fixButton = async () => {
  if (document.body.clientWidth <= 767 /* Breakpoint $m */) {
    let ucRoot = document.querySelector('#usercentrics-root');
    while (!ucRoot) {
      await sleep(500);
      ucRoot = document.querySelector('#usercentrics-root');
    }
    await sleep(100);
    const root = ucRoot.shadowRoot!;
    await sleep(500);
    let button = root.querySelectorAll('[data-testid=uc-privacy-button]');
    while (button.length === 0) {
      await sleep(500);
      button = root.querySelectorAll('[data-testid=uc-privacy-button]');
    }
    if (button[0]) {
      button[0].setAttribute(
        'style',
        'top: 8px !important; box-shadow: none !important; right: 64px !important; left: auto !important; background: #023e84 !important;'
      );
    }
  }
};

type PageViewEvent = {
  event: 'pageview';
  pageID: 'pro';
  pageCountry: string;
  pageLanguage: string;
  businessUnit: 'dining_lifestyle';
  customerGroup: 'retailer' | 'vb employee';
};
type VirtualPageViewEvent = {
  event: 'virtualpageview';
  category: 'bpl app';
  page:
    | '/applic/bpl-app/virtual/show-digital-bag'
    | '/applic/bpl-app/virtual/show-gtin-codes'
    | '/applic/bpl-app/virtual/show-filters';
};
type LoginEvent = {
  event: 'account';
  category: 'bpl app';
  customerGroup: 'retailer' | 'vb employee';
  step: 'login';
};
type DBCreateEvent = {
  event: 'digital bag';
  action: 'create';
  cartCurrency: string;
  cartValue: number;
  category: 'bpl app';
};
type DBUpdateAddEvent = {
  event: 'digital bag';
  action: 'update - add';
  cartCurrency: string;
  cartValue: number;
  category: 'bpl app';
};
type DBUpdateRemoveEvent = {
  event: 'digital bag';
  action: 'update - remove';
  cartCurrency: string;
  cartValue: number;
  category: 'bpl app';
};
type DBUpdateEmptyEvent = {
  event: 'digital bag';
  action: 'update - empty';
  cartCurrency: string;
  cartValue: 0.0;
  category: 'bpl app';
};
type B2BTransferEvent = {
  event: 'digital bag';
  action:
    | 'create servicewelt b2b shop cart'
    | 'update servicewelt b2b shop cart';
  cartCurrency: string;
  cartValue: number;
  category: 'bpl app';
};
type TrackingEvent =
  | PageViewEvent
  | VirtualPageViewEvent
  | LoginEvent
  | DBCreateEvent
  | DBUpdateAddEvent
  | DBUpdateRemoveEvent
  | DBUpdateEmptyEvent
  | B2BTransferEvent;

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService implements OnDestroy {
  // Apple App Tracking Transparency
  private appTrackingAllowed = false;
  private didAcceptCookieBanner = false;
  private didLoadAnalytics = false;

  private subscriptions: Subscription[] = [];
  private el?: HTMLScriptElement;
  private language = this.languageService.i18NextService.language;

  canTrack() {
    return (
      ((this.native.native.mode > BridgeMode.Mock && this.appTrackingAllowed) ||
        this.native.native.mode <= BridgeMode.Mock) &&
      this.didAcceptCookieBanner
    );
  }

  constructor(
    private native: NativeIntegrationService,
    private languageService: LanguageService,
    private dataService: DataService,
    private router: Router,
    private collectionService: CollectionsService
  ) {
    this.subscriptions.push(
      languageService.i18NextService.events.languageChanged.subscribe((x) => {
        this.language = x;
        if (this.el) {
          this.el!.dataset.languageCode = x;
        }
      })
    );
    this.subscriptions.push(
      router.events.subscribe((e) => {
        if (e instanceof NavigationEnd) {
          if (e.url.includes('pack-download')) {
            return;
          }
          // @ts-ignore
          const event = this.WithLanguage({
            event: 'pageview',
            pageID: 'pro',
            businessUnit: 'dining_lifestyle',
          });
          if (event) {
            this.trackEventWithCustomerGroup(event);
          }
        }
      })
    );

    this.native.spinTillReady().then(async () => {
      if (this.native.native.mode > BridgeMode.Mock) {
        // Try catch weil hasTrackingPermission$ nicht auf android existiert
        try {
          const result = await this.native.permissions.hasTrackingPermission$();
          console.log(`tracking permission status`, result);
          // Hier Granted , Unknown oder Denied.
          // Bei Unknown nachfragen
          // Denied ist das default
          // Nur iOS: Bei android immer Granted
          if (result.data === PermissionState.Granted) {
            this.appTrackingAllowed = true;
            this.loadCookieBanner();
          } else if (result.data === PermissionState.Unknown) {
            const permissionResult =
              await this.native.permissions.requestTrackingPermission$();
            // Hier nur Granted oder Denied.
            // Denied ist das default
            if (permissionResult.data === PermissionState.Granted) {
              this.appTrackingAllowed = true;
              this.loadCookieBanner();
            }
          }
        } catch (e) {
          console.error(e);
          this.appTrackingAllowed = true;
          this.loadCookieBanner();
        }
      } else {
        // Web
        this.appTrackingAllowed = true;
        this.loadCookieBanner();
      }
    });

    window.addEventListener('ucEvent', (e) => this.onConsentChange(e));
  }

  private loadCookieBanner() {
    this.el = document.createElement('script');
    this.el.src =
      'https://consent.villeroy-boch.com/production/vb_dl_villeroy_boch_com/consent-manager.js';
    this.el.id = 'vb-consent-manager';
    this.el.dataset.languageCode = this.language;
    this.el.onload = fixButton;
    document.addEventListener('resize', fixButton);
    document.body.appendChild(this.el);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((x) => x.unsubscribe);
    window.removeEventListener('ucEvent', (e) => this.onConsentChange(e));
    document.removeEventListener('resize', fixButton);
  }

  private onConsentChange(e: Event) {
    // @ts-expect-error Keine types :(
    if (e.detail && e.detail.event == 'consent_status') {
      const gtmName = 'Google Tag Manager';
      if (
        // @ts-expect-error Keine types :(
        e.detail.hasOwnProperty(gtmName)
      ) {
        this.didAcceptCookieBanner =
          // @ts-expect-error Keine types :(
          e.detail[gtmName];
        // @ts-expect-error Keine types :(
        window.gtag('consent', 'update', {
          analytics_storage:
            // @ts-expect-error Keine types :(
            e.detail[gtmName] ? 'granted' : 'denied',
        });

        // @ts-expect-error Keine types :(
        if (e.detail[gtmName]) {
          this.loadGoogleAnalytics();
        } else {
          if (this.didLoadAnalytics) {
            window.location.reload();
          }
        }
      }
    }
  }

  public loadGoogleAnalytics() {
    if (this.didLoadAnalytics) {
      return;
    }
    // @ts-ignore
    if (typeof window.dataLayer === 'undefined' || window.dataLayer === null) {
      // @ts-ignore
      window.dataLayer = [];
    }
    // @ts-ignore
    let f = function (w, d, s, l, i) {
      w[l] = w[l] || [];
      w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      var f = d.getElementsByTagName(s)[0],
        j = d.createElement(s),
        dl = l != 'dataLayer' ? '&l=' + l : '';
      j.async = true;
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
      f.parentNode.insertBefore(j, f);
    };
    f(window, document, 'script', 'dataLayer', 'GTM-W8RBS');
    // @ts-ignore
    window.dataLayer.push({ dataLayerStatus: 'initialized' });
    this.didLoadAnalytics = true;
  }

  public trackEvent(data: TrackingEvent) {
    if (this.canTrack()) {
      // @ts-expect-error gtag wird dynamisch definiert
      window.dataLayer.push(data);
    }
  }

  public trackEventWithCurrency(data: Partial<TrackingEvent>) {
    if (this.canTrack()) {
      // @ts-expect-error gtag wird dynamisch definiert
      window.dataLayer.push({
        cartCurrency: this.collectionService.currencyCode,
        ...data,
      });
    }
  }

  public trackEventWithLanguage(
    data: Omit<PageViewEvent, 'pageCountry' | 'pageLanguage'>
  ) {
    if (this.canTrack()) {
      // @ts-expect-error gtag wird dynamisch definiert
      window.dataLayer.push({
        pageCountry: this.languageService.i18NextService.language.toUpperCase(),
        pageLanguage:
          this.languageService.i18NextService.language.toUpperCase(),
        ...data,
      });
    }
  }

  public WithLanguage(
    data: Omit<PageViewEvent, 'pageCountry' | 'pageLanguage'>
  ) {
    if (this.canTrack()) {
      return {
        pageCountry: this.languageService.i18NextService.language.toUpperCase(),
        pageLanguage:
          this.languageService.i18NextService.language.toUpperCase(),
        ...data,
      };
    }
    return null;
  }

  public trackEventWithCustomerGroup(
    data: Omit<LoginEvent | PageViewEvent, 'customerGroup'>
  ) {
    if (this.canTrack()) {
      if (!this.dataService.context) {
        return;
      }
      /*
zu Ihrer Frage, wodurch kann man Interner zu Externer App-User unterscheiden (Tracking-Thema)
bekam ich von unserer IT folgende Info:

Es gibt 2 Werte, an denen man es festmachen kann:  „priceListType“ und „priceListMarket“

Bei Internen Usern (also V&B-Mitarbeitern) ist priceListType null und priceListMarket leer:

"priceListType": null,
"priceListMarket": "",

Bei Externen Usern (Kunden ) kommen da Werte mit.

Bsp:

"priceListType": 01,
"priceListMarket": "100",
      */
      const userType = this.dataService.context?.priceListType
        ? 'retailer'
        : 'vb employee';
      // @ts-expect-error gtag wird dynamisch definiert
      window.dataLayer.push({
        customerGroup: userType,
        ...data,
      });
    }
  }
}
